<template>
  <div class="attachment-upload-row">
    <b-row class="attachment-upload-row__fields align-items-end">
      <!-- Document name with suggestions -->
      <b-col
        cols="12"
        sm="5"
        md="5"
        lg="5"
        class="attachment-upload-row__name mb-2 mb-sm-0"
      >
        <validation-provider
          #default="{ errors }"
          :name="nameFieldName"
          :vid="nameVid"
          :rules="nameRules"
        >
          <b-form-group
            :label="nameLabel"
            :label-for="nameInputId"
          >
            <b-input-group class="input-group-merge form-send-message mr-1">
              <b-form-input
                :id="nameInputId"
                :value="value.name"
                :disabled="disabled"
                placeholder="الاسم "
                :list="datalistId"
                @input="onNameInput"
              />
              <datalist :id="datalistId">
                <option
                  v-for="type in mediaTypes"
                  :key="type"
                  :value="type"
                >
                  {{ type }}
                </option>
              </datalist>
            </b-input-group>
          </b-form-group>
          <small class="text-danger float-left">{{ errors[0] }}</small>
        </validation-provider>
      </b-col>

      <!-- File upload -->
      <b-col
        cols="12"
        sm="5"
        md="5"
        lg="5"
        class="attachment-upload-row__file mb-2 mb-sm-0"
      >
        <validation-provider
          #default="{ errors }"
          :name="fileFieldName"
          :vid="fileVid"
          :rules="fileRules"
        >
          <b-form-group
            :label="fileLabel"
            :label-for="fileInputId"
          >
            <b-form-file
              :id="fileInputId"
              v-model="localFile"
              :disabled="disabled"
              :state="Boolean(value.file)"
              placeholder="اختر ملف"
              drop-placeholder="اسحب الملف هنا"
              browse-text="تصفح"
              :accept="accept"
            />
          </b-form-group>
          <small class="text-danger float-left">{{ errors[0] }}</small>
        </validation-provider>
      </b-col>

      <!-- Add / remove actions -->
      <b-col
        cols="12"
        sm="2"
        md="2"
        lg="2"
        class="attachment-upload-row__actions"
      >
        <div
          class="attachment-upload-row__actions-inner"
          role="group"
          :aria-label="actionsAriaLabel"
        >
          <button
            v-if="canRemove"
            type="button"
            class="attachment-upload-row__action-btn attachment-upload-row__action-btn--remove"
            :disabled="disabled"
            :aria-label="removeAriaLabel"
            @click="$emit('remove')"
          >
            <feather-icon
              icon="MinusIcon"
              class="text-danger"
              size="20"
            />
          </button>
          <button
            v-if="showAdd"
            type="button"
            class="attachment-upload-row__action-btn attachment-upload-row__action-btn--add"
            :disabled="disabled"
            :aria-label="addAriaLabel"
            @click="$emit('add')"
          >
            <feather-icon
              icon="PlusIcon"
              class="text-primary"
              size="20"
            />
          </button>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import {
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormFile,
  BInputGroup,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

export const DEFAULT_ATTACHMENT_ACCEPT = '.dwg,.dxf,.txt,.kml,.kmz,.pdf,.jpg,.jpeg,.png,.img,.zip.rar.application/x-rar'

export default {
  name: 'AttachmentUploadRow',
  components: {
    ValidationProvider,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormFile,
    BInputGroup,
    FeatherIcon,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    mediaTypes: {
      type: Array,
      default: () => [],
    },
    nameRules: {
      type: [String, Object],
      default: 'required',
    },
    fileRules: {
      type: [String, Object],
      default: 'required',
    },
    nameLabel: {
      type: String,
      default: '*الاسم',
    },
    fileLabel: {
      type: String,
      default: 'الملف ',
    },
    accept: {
      type: String,
      default: DEFAULT_ATTACHMENT_ACCEPT,
    },
    canRemove: {
      type: Boolean,
      default: false,
    },
    showAdd: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      default: 'attachment-upload',
    },
  },
  computed: {
    rowKey() {
      return `${this.idPrefix}-${this.index}`
    },
    nameInputId() {
      return `${this.rowKey}-name`
    },
    fileInputId() {
      return `${this.rowKey}-file`
    },
    datalistId() {
      return `${this.rowKey}-media-types`
    },
    nameVid() {
      return `${this.rowKey}-name`
    },
    fileVid() {
      return `${this.rowKey}-file`
    },
    nameFieldName() {
      return `name-${this.index}`
    },
    fileFieldName() {
      return `file-${this.index}`
    },
    actionsAriaLabel() {
      return 'إجراءات الصف'
    },
    removeAriaLabel() {
      return 'حذف صف المرفق'
    },
    addAriaLabel() {
      return 'إضافة صف مرفق'
    },
    localFile: {
      get() {
        return this.value.file
      },
      set(file) {
        this.emitUpdate({ file })
      },
    },
  },
  methods: {
    emitUpdate(patch) {
      this.$emit('input', { ...this.value, ...patch })
    },
    onNameInput(name) {
      this.emitUpdate({ name })
    },
  },
}
</script>

<style scoped>
/* Align action buttons with labeled inputs (form-group label height) */
.attachment-upload-row__actions-inner {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  width: 100%;
  min-height: 2.75rem;
  padding-top: 1.75rem;
}

.attachment-upload-row__action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.75rem;
  height: 2.75rem;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 0.357rem;
  background: transparent;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.attachment-upload-row__action-btn:hover:not(:disabled) {
  background-color: rgba(0, 0, 0, 0.04);
}

.attachment-upload-row__action-btn:focus {
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(6, 40, 77, 0.25);
}

.attachment-upload-row__action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* xs: stacked fields — action bar below, separated, end-aligned (RTL-friendly) */
@media (max-width: 575.98px) {
  .attachment-upload-row__actions {
    margin-top: 0.25rem;
    padding-top: 0.5rem;
    border-top: 1px solid #ebe9f1;
  }

  .attachment-upload-row__actions-inner {
    justify-content: flex-end;
    padding-top: 0;
    min-height: auto;
  }

  .attachment-upload-row__action-btn {
    width: 3rem;
    height: 3rem;
  }
}

/* sm+: name(5) + file(5) + actions(2) on one row */
@media (min-width: 576px) and (max-width: 767.98px) {
  .attachment-upload-row__actions-inner {
    justify-content: center;
    padding-top: 1.75rem;
  }
}

/* md+ */
@media (min-width: 768px) {
  .attachment-upload-row__actions-inner {
    justify-content: center;
    padding-top: 1.75rem;
  }
}
</style>
